import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Participant } from '@/features/stm/lib/types'
import { toast } from 'sonner'

import { authFetch } from '@/shared/lib/auth-fetch'

async function stmFetch(url: string, init?: RequestInit) {
  const res = await authFetch(url, init)
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `HTTP ${res.status}`)
  }
  return res.json()
}

export function useStmParticipants(groupId: string | null) {
  return useQuery({
    queryKey: ['stm', 'participants', groupId],
    queryFn: async () => {
      if (!groupId) return []
      return stmFetch(`/api/stm/participants?groupId=${encodeURIComponent(groupId)}`)
    },
    enabled: !!groupId,
  })
}

export function useStmCreateParticipant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (p: Omit<Participant, 'id' | 'created_at' | 'updated_at'>) => {
      return stmFetch('/api/stm/participants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(p),
      })
    },
    onSuccess: (data: Participant) => {
      qc.invalidateQueries({ queryKey: ['stm', 'participants', data.group_id] })
      toast.success('Participant added')
    },
    onError: (e: Error) => toast.error('Failed to add participant: ' + e.message),
  })
}

export function useStmUpdateParticipant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Participant> & { id: string }) => {
      return stmFetch('/api/stm/participants', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...updates }),
      }) as Promise<Participant>
    },
    onSuccess: (data: Participant) => {
      qc.invalidateQueries({ queryKey: ['stm', 'participants', data.group_id] })
      toast.success('Participant updated')
    },
    onError: (e: Error) => toast.error('Failed to update participant: ' + e.message),
  })
}

export function useStmDeleteParticipant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, groupId }: { id: string; groupId: string }) => {
      await stmFetch('/api/stm/participants', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, groupId }),
      })
    },
    onSuccess: (_, { groupId }) => {
      qc.invalidateQueries({ queryKey: ['stm', 'participants', groupId] })
      toast.success('Participant removed')
    },
    onError: (e: Error) => toast.error('Failed to remove participant: ' + e.message),
  })
}
